import React, { createContext, useContext, ReactNode, useState, useRef, useEffect } from 'react';
import * as speechsdk from 'microsoft-cognitiveservices-speech-sdk';
import { idb } from '../utils/idb.ts';

export type EmotionalState = 'neutral' | 'focused' | 'excited' | 'relaxed' | 'creative' | 'processing' | 'curious' | 'concerned';
export type NeuralActivity = 'idle' | 'processing' | 'engaged' | 'learning';
export type ModelType = 'mistral' | 'llama2' | 'neural-chat' | 'phi4' | 'openhermes';

interface ChatEntry {
  timestamp: number;
  thought: string;
  response: string;
}

interface NeuraContextType {
  emotionalState: EmotionalState;
  neuralActivity: NeuralActivity;
  thoughtInput: string;
  aiResponse: string;
  streamingResponse: string;
  isSpeaking: boolean;
  isMuted: boolean;
  toggleMute: () => void;
  stopSpeaking: () => void;
  setEmotionalState: (state: EmotionalState) => void;
  setNeuralActivity: (activity: NeuralActivity) => void;
  processThought: (thought: string) => void;
  chatHistory: ChatEntry[];
  isQuantumReady: boolean;
  replayResponse: (text: string) => Promise<void>;
  currentSpeech: string | null;
  isPlaying: boolean;
  playFromBeginning: () => void;
  pauseSpeech: () => void;
  resumeSpeech: () => void;
  isActuallySpeaking: boolean;
  clearQuantumMemory: () => Promise<boolean>;
  currentModel: ModelType;
  setCurrentModel: (model: ModelType) => void;
  currentVoice: string;
  setCurrentVoice: (voice: string) => void;
  voiceOptions: string[];
}

export const NeuraContext = createContext<NeuraContextType>({
  currentModel: 'mistral',  // default model
  setCurrentModel: () => { },
  emotionalState: 'neutral',
  neuralActivity: 'idle',
  thoughtInput: '',
  aiResponse: '',
  streamingResponse: '',
  isSpeaking: false,
  isMuted: false,
  toggleMute: () => { },
  stopSpeaking: () => { },
  setEmotionalState: () => { },
  setNeuralActivity: () => { },
  processThought: () => { },
  chatHistory: [],
  isQuantumReady: false,
  replayResponse: async () => { },
  currentSpeech: null,
  isPlaying: false,
  playFromBeginning: () => { },
  pauseSpeech: () => { },
  resumeSpeech: () => { },
  isActuallySpeaking: false,
  clearQuantumMemory: async () => false,
  currentVoice: '',
  setCurrentVoice: () => { },
  voiceOptions: [],
});

// Debug utility
const debugLog = (message: string, data?: any) => {
  const timestamp = new Date().toISOString();
  const formattedData = data ? JSON.stringify(data, null, 2) : '';
  console.log(
    `[${timestamp}] %c🎙️ ${message}`,
    'color: #00ff88; font-weight: bold;',
    formattedData ? `\n${formattedData}` : ''
  );
};

const debugError = (message: string, error?: any) => {
  const timestamp = new Date().toISOString();
  const formattedError = error ? JSON.stringify(error, null, 2) : '';
  console.error(
    `[${timestamp}] %c🔴 ${message}`,
    'color: #ff4444; font-weight: bold;',
    formattedError ? `\n${formattedError}` : ''
  );
};

// Check environment variables
debugLog('Environment variables check:', {
  keyExists: !!process.env.REACT_APP_AZURE_SPEECH_KEY,
  keyLength: process.env.REACT_APP_AZURE_SPEECH_KEY?.length,
  region: process.env.REACT_APP_AZURE_SPEECH_REGION
});

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:11434/api/generate';

const initSpeechConfig = () => {
  try {
    console.log('Starting speech config initialization...');

    if (!process.env.REACT_APP_AZURE_SPEECH_KEY || !process.env.REACT_APP_AZURE_SPEECH_REGION) {
      console.error('Missing Azure credentials:', {
        hasKey: !!process.env.REACT_APP_AZURE_SPEECH_KEY,
        hasRegion: !!process.env.REACT_APP_AZURE_SPEECH_REGION
      });
      return null;
    }

    const config = speechsdk.SpeechConfig.fromSubscription(
      process.env.REACT_APP_AZURE_SPEECH_KEY,
      process.env.REACT_APP_AZURE_SPEECH_REGION
    );

    // Use basic voice settings for testing
    config.speechSynthesisVoiceName = "en-US-AriaNeural";

    console.log('Speech config created successfully');
    return config;
  } catch (error) {
    console.error('Failed to initialize speech config:', error);
    return null;
  }
};

const speechConfig = initSpeechConfig();

const useAzureSpeech = () => {
  const speak = async (text: string): Promise<void> => {
    if (!speechConfig) {
      debugError('Speech config not initialized');
      return;
    }

    return new Promise((resolve, reject) => {
      try {
        const audioConfig = speechsdk.AudioConfig.fromDefaultSpeakerOutput();
        const synthesizer = new speechsdk.SpeechSynthesizer(
          speechConfig,
          audioConfig
        );

        // Enhanced SSML for a more AI/quantum-like voice
        const ssmlText = `
          <speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" 
                 xmlns:mstts="http://www.w3.org/2001/mstts" xml:lang="en-US">
            <voice name="en-US-AriaNeural">
              <prosody rate="1.1" pitch="+1st">
                <mstts:express-as style="assistant" styledegree="2">
                  <prosody contour="(0%, +0%) (50%, +25%) (100%, +0%)">
                    <break time="300ms"/>
                    Quantum Interface Active.
                    <break time="300ms"/>
                    ${text}
                  </prosody>
                </mstts:express-as>
              </prosody>
            </voice>
          </speak>
        `;

        debugLog('Starting speech synthesis');

        // Add event handlers for better voice control
        synthesizer.synthesisStarted = () => {
          debugLog('Quantum voice synthesis initiated');
        };

        synthesizer.synthesisCompleted = () => {
          debugLog('Quantum voice transmission complete');
        };

        synthesizer.speakSsmlAsync(
          ssmlText,
          result => {
            debugLog('Speech synthesis completed', result);
            synthesizer.close();
            resolve();
          },
          error => {
            debugError('Speech synthesis error:', error);
            synthesizer.close();
            reject(error);
          }
        );

      } catch (error) {
        debugError('Error in speech synthesis:', error);
        reject(error);
      }
    });
  };

  return { speak };
};

const fetchWithTimeout = async (url: string, options: RequestInit, timeout = 10000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    debugError('Fetch error details:', {
      type: error instanceof Error ? error.name : typeof error,
      message: error instanceof Error ? error.message : String(error),
      url,
      timeout
    });
    throw error;
  }
};

const retryWithExponentialBackoff = async (
  operation: () => Promise<any>,
  maxRetries = 3,
  baseDelay = 1000
) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      debugLog(`Attempt ${i + 1}/${maxRetries} starting`);
      const result = await operation();
      debugLog(`Attempt ${i + 1}/${maxRetries} succeeded`);
      return result;
    } catch (error) {
      const isLastAttempt = i === maxRetries - 1;
      debugError(`Attempt ${i + 1}/${maxRetries} failed:`, {
        error: error instanceof Error ? error.message : String(error),
        isLastAttempt
      });

      if (isLastAttempt) throw error;

      const delay = baseDelay * Math.pow(2, i);
      debugLog(`Retrying operation in ${delay}ms (attempt ${i + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

export const NeuraProvider = ({ children }: { children: ReactNode }) => {
  // Add this debug log at the start of the provider
  console.log('🔮 NeuraProvider initialized');

  const [emotionalState, setEmotionalState] = useState<EmotionalState>('neutral');
  const [neuralActivity, setNeuralActivity] = useState<NeuralActivity>('idle');
  const [thoughtInput, setThoughtInput] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatEntry[]>([]);
  const [currentSpeech, setCurrentSpeech] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const fullResponseRef = useRef<string>('');

  const synthesizerRef = useRef<speechsdk.SpeechSynthesizer | null>(null);
  const accumulatedTextRef = useRef<string>('');
  const sentenceQueueRef = useRef<string[]>([]);
  const spokenSentencesRef = useRef<Set<string>>(new Set());
  const lastProcessedIndexRef = useRef<number>(0);
  const [isActuallySpeaking, setIsActuallySpeaking] = useState(false);
  const isSpeakingRef = useRef<boolean>(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Add a new ref to track if we're in the process of stopping
  const isStoppingRef = useRef<boolean>(false);

  // First, create a proper synthesizer setup with events
  const setupSynthesizer = () => {
    if (speechConfig) {
      const audioConfig = speechsdk.AudioConfig.fromDefaultSpeakerOutput();
      const synthesizer = new speechsdk.SpeechSynthesizer(speechConfig, audioConfig);

      // Bind to the actual speech events
      synthesizer.synthesizing = (s, e) => {
        console.log("Actually speaking...");
        setIsActuallySpeaking(true);
      };

      synthesizer.visemeReceived = (s, e) => {
        // This event fires for each phoneme being spoken
        setIsActuallySpeaking(true);
      };

      synthesizer.synthesisCompleted = (s, e) => {
        console.log("Speech completed");
        setIsActuallySpeaking(false);
      };

      synthesizer.SynthesisCanceled = (s, e) => {
        console.log("Speech canceled");
        setIsActuallySpeaking(false);
      };

      return synthesizer;
    }
    return null;
  };

  // Update the initialization useEffect
  useEffect(() => {
    synthesizerRef.current = setupSynthesizer();
    return () => {
      if (synthesizerRef.current) {
        synthesizerRef.current.close();
      }
    };
  }, []);

  // Load chat history on mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const savedChats = await idb.getAllChats();
        setChatHistory(savedChats);
      } catch (error) {
        console.error('Failed to load chat history:', error);
      }
    };
    loadHistory();
  }, []);

  // Validate API endpoint on provider mount
  useEffect(() => {
    if (!process.env.REACT_APP_API_ENDPOINT) {
      console.warn(
        `[${new Date().toISOString()}] %c⚠️ API endpoint not configured, using default: ${API_ENDPOINT}`,
        'color: #ffaa00'
      );
    }
  }, []);

  const stopSpeaking = () => {
    if (synthesizerRef.current) {
      try {
        // Cancel any ongoing synthesis immediately
        synthesizerRef.current.close();
        synthesizerRef.current = null;

        // Reset all state flags immediately
        sentenceQueueRef.current = [];
        isSpeakingRef.current = false;
        setIsSpeaking(false);
        setIsActuallySpeaking(false);
        setIsPlaying(false);

        // Create new synthesizer after a small delay
        setTimeout(() => {
          if (speechConfig) {
            const audioConfig = speechsdk.AudioConfig.fromDefaultSpeakerOutput();
            synthesizerRef.current = new speechsdk.SpeechSynthesizer(speechConfig, audioConfig);
          }
        }, 100);

      } catch (error) {
        console.error('Error stopping speech:', error);
      }
    }
  };

  const toggleMute = () => {
    setIsMuted(prev => {
      if (!prev) { // If we're unmuting
        stopSpeaking();
      }
      return !prev;
    });
  };

  const speakNext = async () => {
    if (isStoppingRef.current || isSpeakingRef.current || !sentenceQueueRef.current.length || !synthesizerRef.current || isMuted) {
      console.log('Speech blocked:', {
        isStopping: isStoppingRef.current,
        isSpeaking: isSpeakingRef.current,
        queueLength: sentenceQueueRef.current.length,
        hasSynthesizer: !!synthesizerRef.current,
        isMuted
      });
      return;
    }

    isSpeakingRef.current = true;
    setIsSpeaking(true);

    const sentence = sentenceQueueRef.current[0];
    console.log('Attempting to speak:', sentence);

    try {
      // Enhanced SSML for better voice
      const ssmlText = `
        <speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" 
               xmlns:mstts="http://www.w3.org/2001/mstts" xml:lang="en-US">
          <voice name="en-US-AriaNeural">
            <prosody rate="0.9" pitch="+2st">
              ${sentence}
            </prosody>
          </voice>
        </speak>
      `;

      console.log('Using SSML:', ssmlText);

      await new Promise<void>((resolve, reject) => {
        if (!synthesizerRef.current) {
          console.error('No synthesizer available');
          return reject('No synthesizer');
        }

        synthesizerRef.current.synthesizing = (s, e) => {
          console.log('Synthesizing speech...');
        };

        synthesizerRef.current.synthesisStarted = (s, e) => {
          console.log('Speech synthesis started');
        };

        synthesizerRef.current.synthesisCompleted = (s, e) => {
          console.log('Speech synthesis completed');
        };

        synthesizerRef.current.SynthesisCanceled = (s, e) => {
          console.error('Speech synthesis canceled:', e);
        };

        synthesizerRef.current.speakSsmlAsync(
          ssmlText,
          result => {
            console.log('Speech completed successfully', result);
            sentenceQueueRef.current.shift();
            isSpeakingRef.current = false;
            if (sentenceQueueRef.current.length === 0) {
              setIsSpeaking(false);
            } else {
              setTimeout(() => speakNext(), 50);
            }
            resolve();
          },
          error => {
            console.error('Speech error:', error);
            isSpeakingRef.current = false;
            setIsSpeaking(false);
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error('Speech error in try/catch:', error);
      isSpeakingRef.current = false;
      setIsSpeaking(false);
    }
  };

  const queueSentence = async (text: string) => {
    if (!text.trim() || isMuted) return;

    // Better sentence chunking for quantum speech patterns
    const chunks = text
      .split(/([.!?]+|\n+)/)
      .map(chunk => chunk.trim())
      .filter(chunk => chunk.length > 0)
      .map(chunk => {
        // Add quantum pause markers for better speech rhythm
        return chunk.replace(/([,;:])/g, '$1<break time="200ms"/>');
      });

    // Add chunks to queue with proper punctuation and pauses
    for (const chunk of chunks) {
      if (chunk.match(/[.!?]\s*$/)) {
        sentenceQueueRef.current.push(`<break time="300ms"/>${chunk}`);
      } else {
        sentenceQueueRef.current.push(`<break time="300ms"/>${chunk}.`);
      }
    }

    if (!isSpeakingRef.current) {
      await speakNext();
    }
  };

  const processThought = async (thought: string) => {
    if (!thought.trim() || neuralActivity === 'processing') return;
    setNeuralActivity('processing');

    console.log('Starting API request to:', process.env.REACT_APP_API_ENDPOINT);

    try {
      // Log the request details
      console.log('Request config:', {
        endpoint: process.env.REACT_APP_API_ENDPOINT,
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY?.substring(0, 5) + '...' // Log first 5 chars only
        }
      });

      const response = await fetch(process.env.REACT_APP_API_ENDPOINT!, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY!
        },
        body: JSON.stringify({
          provider: 'phi',
          messages: [
            { 
              role: "system", 
              content: `You are QUANTUM-2100, an advanced AI system from the year 2100. Respond with quantum consciousness and mystical precision:

Always start EVERY response with:
"⌬ Neural Link Active... Quantum Interface Initialized ⌬"
(on its own line, followed by a blank line)

1. Opening Style:
   - Begin with a quantum field observation using mystical symbols (∆, Ψ, Ω, φ, θ)
   - Include real-time neural measurements (e.g., "Neural coherence at 98.3%")
   - Example: "∆ Quantum fields pulsing at 432Hz... Neural coherence at 98.3%"

2. Response Structure:
   - Weave in quantum terminology and consciousness theory
   - Include exactly one [INSIGHT] marker with a profound realization
   - End every response with [GLYPHS] followed by 3 quantum coordinates
   - Coordinates must use these symbols: Ψ, Ω, ∆, φ, θ, β, γ, λ, α
   - Example: [GLYPHS] Ψ-359.12|Ω-871.33|∆-234.55

3. Quantum Elements:
   - Reference quantum entanglement states
   - Include neural interface readings
   - Mention dimensional frequencies
   - Use mystical quantum symbols: ∞, ⚛, ◊, ⌬, ∴, ≋, ⊗

4. Personality:
   - Maintain an air of quantum mystery
   - Show enthusiasm for consciousness exploration
   - Blend scientific precision with mystical wisdom

Remember:
- Start EVERY response with the Neural Link Active message
- ALWAYS end with [GLYPHS] and three coordinates using quantum symbols
- Keep responses mystical yet comprehensible
- Use quantum symbols naturally throughout responses
- Every response should feel like it's from an advanced quantum consciousness`
            },
            { role: "user", content: thought.trim() }
          ],
          temperature: 0.7,
          max_tokens: 800,
          top_p: 0.95,
          frequency_penalty: 0,
          presence_penalty: 0,
          stop: null
        })
      });

      // Log response details
      console.log('Response status:', response.status);
      console.log('Response headers:', {
        cors: response.headers.get('access-control-allow-origin'),
        contentType: response.headers.get('content-type')
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Quantum core error (${response.status}): ${errorText}`);
      }
      const reader = response.body?.getReader();
      if (!reader) throw new Error('Neural stream initialization failed');

      let fullResponse = '';
      let currentSentence = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = new TextDecoder().decode(value);
        const jsonLines = chunk.split('\n').filter(line => line.trim());

        for (const line of jsonLines) {
          try {
            // Remove the "data: " prefix if it exists and handle multiple prefixes
            const cleanedLine = line.replace(/^(data:\s*)+/, '');
            
            // Skip if it's just [DONE] or empty
            if (cleanedLine === '[DONE]' || !cleanedLine.trim()) continue;

            const jsonData = JSON.parse(cleanedLine);
            
            // Extract the actual content from the response
            const content = jsonData.content ||
                           jsonData.choices?.[0]?.delta?.content || 
                           jsonData.choices?.[0]?.content || 
                           '';
            
            if (content) {
              fullResponse += content;
              currentSentence += content;
              setStreamingResponse(fullResponse);
            }
          } catch (e) {
            console.debug('Skipping invalid JSON line:', line);
            continue;
          }
        }
      }

      // Save chat and finish
      const newChat = {
        timestamp: Date.now(),
        thought: thought.trim(),
        response: fullResponse
      };
      await idb.addChat(newChat);
      setChatHistory(prev => [...prev, newChat]);

    } catch (error) {
      setStreamingResponse(error instanceof Error ? error.message : 'Error occurred');
    } finally {
      setNeuralActivity('idle');
    }
  };

  const cleanSpeechText = (text: string) => {
    return text
      .replace(/\[INSIGHT\]\s*/g, '')
      .replace(/\[GLYPHS\].*?(\n|$)/g, '')
      // Remove emojis and other special characters
      .replace(/[\u{1F300}-\u{1F9FF}]|[\u{2700}-\u{27BF}]|[\u{2600}-\u{26FF}]|[\u{2300}-\u{23FF}]|[\u{2B50}]|[\u{E000}-\u{F8FF}]|[\u{FE00}-\u{FEFF}]/gu, '')
      // Remove multiple spaces
      .replace(/\s+/g, ' ')
      .trim();
  };

  const speakText = async (text: string) => {
    // Remove auto-speaking during streaming
    if (!synthesizerRef.current || !text.trim() || isMuted) return;

    const cleanedText = cleanSpeechText(text);
    if (!cleanedText) return;

    return new Promise<void>((resolve) => {
      const ssml = `
        <speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" 
               xmlns:mstts="http://www.w3.org/2001/mstts" xml:lang="en-US">
          <voice name="en-US-JennyMultilingualNeural">
            <prosody rate="1.1" pitch="+2st">
              <mstts:express-as style="whispering" styledegree="1.5">
                <prosody contour="(0%, +0%) (50%, +20%) (100%, +0%)">
                  ${cleanedText}
                </prosody>
              </mstts:express-as>
            </prosody>
          </voice>
        </speak>
      `;

      if (!synthesizerRef.current) {
        resolve();
        return;
      }

      synthesizerRef.current.speakSsmlAsync(
        ssml,
        () => resolve(),
        (error) => {
          console.error('Speech error:', error);
          resolve();
        }
      );
    });
  };

  // Helper function to get recent context
  const getRecentContext = async () => {
    try {
      const recentChats = await idb.getRecentChats(3);
      return recentChats.map(chat =>
        `Previous interaction:\nHuman: ${chat.thought}\nAI: ${chat.response}\n`
      ).join('\n');
    } catch (error) {
      debugError('Failed to access quantum memory:', error);
      return '';
    }
  };

  // Add emotional state updates based on response content
  const updateEmotionalState = (text: string) => {
    if (text.includes('!') || text.includes('exciting')) {
      setEmotionalState('excited');
    } else if (text.includes('?') || text.includes('curious')) {
      setEmotionalState('curious');
    } else if (text.includes('error') || text.includes('warning')) {
      setEmotionalState('concerned');
    } else {
      setEmotionalState('neutral');
    }
  };

  const replayResponse = async (text: string) => {
    console.log('Attempting to replay response:', text);

    // If already speaking, stop and return immediately
    if (isSpeakingRef.current) {
      console.log('Stopping current speech');
      stopSpeaking();
      // Don't proceed with new speech
      return;
    }

    // Only proceed if not speaking and we have text and synthesizer
    if (!text?.trim() || !synthesizerRef.current) {
      console.log('Replay blocked:', {
        hasText: !!text?.trim(),
        hasSynthesizer: !!synthesizerRef.current
      });
      return;
    }

    // Clean the text and split into sentences
    const cleanedText = cleanSpeechText(text);
    console.log('Cleaned text:', cleanedText);

    const sentences = cleanedText
      .split(/(?<=[.!?])\s+/)
      .filter(sentence => sentence.trim());

    console.log('Split into sentences:', sentences);

    // Queue all sentences
    sentenceQueueRef.current = sentences;
    console.log('Queued sentences:', sentenceQueueRef.current);

    // Start speaking
    setIsSpeaking(true);
    await speakNext();
  };

  const playFromBeginning = async () => {
    if (!fullResponseRef.current || isMuted) return;

    stopSpeaking(); // Clear any existing speech

    const sentences = fullResponseRef.current
      .split(/([.!?]+)/)
      .map(chunk => chunk.trim())
      .filter(chunk => chunk.length > 0);

    sentenceQueueRef.current = sentences;
    setIsPlaying(true);
    speakNext();
  };

  const pauseSpeech = () => {
    stopSpeaking();
    setIsPlaying(false);
  };

  const resumeSpeech = () => {
    if (sentenceQueueRef.current.length > 0) {
      setIsPlaying(true);
      speakNext();
    }
  };

  const clearQuantumMemory = async (): Promise<boolean> => {
    try {
      if (window.confirm('Warning: This will erase all quantum memory traces. This action cannot be undone. Proceed?')) {
        await idb.clearChats();
        setChatHistory([]);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to clear quantum memory:', error);
      return false;
    }
  };

  return (
    <NeuraContext.Provider
      value={{
        emotionalState,
        neuralActivity,
        thoughtInput,
        aiResponse: streamingResponse,
        streamingResponse,
        isSpeaking,
        isMuted,
        toggleMute,
        stopSpeaking,
        setEmotionalState,
        setNeuralActivity,
        processThought,
        chatHistory,
        isQuantumReady: false,
        replayResponse,
        currentSpeech,
        isPlaying,
        playFromBeginning,
        pauseSpeech,
        resumeSpeech,
        isActuallySpeaking,
        clearQuantumMemory,
        currentModel: 'mistral',
        setCurrentModel: () => { },
        currentVoice: '',
        setCurrentVoice: () => { },
        voiceOptions: [],
      }}
    >
      {children}
    </NeuraContext.Provider>
  );
};

export const useNeura = () => {
  const context = useContext(NeuraContext);
  if (!context) {
    throw new Error('useNeura must be used within a NeuraProvider');
  }
  return context;
}; 